/**
 * Wallet error handling utilities
 * Handles wallet-related promise rejections and errors
 */

/**
 * Checks if an error is wallet-related
 * @param {any} error - The error to check
 * @returns {boolean} - True if it's a wallet error
 */
export const isWalletError = (error) => {
    if (!error || typeof error !== 'object') return false;
    
    // Check for common wallet error patterns
    const walletErrorPatterns = [
        'wallet',
        'account',
        'metamask',
        'web3',
        'ethereum',
        'crypto',
        'blockchain'
    ];
    
    const errorString = JSON.stringify(error).toLowerCase();
    return walletErrorPatterns.some(pattern => errorString.includes(pattern));
};

/**
 * Converts wallet error objects to proper Error instances
 * @param {any} error - The error to convert
 * @param {string} context - Context for the error
 * @returns {Error} - Proper Error instance
 */
export const convertWalletError = (error, context = 'Wallet operation') => {
    if (error instanceof Error) {
        return error;
    }
    
    if (error && typeof error === 'object' && error.code && error.message) {
        // Handle structured wallet errors like { code: 4001, message: "wallet must has at least one account" }
        const errorMessage = `${context} failed (${error.code}): ${error.message}`;
        const walletError = new Error(errorMessage);
        walletError.code = error.code;
        walletError.originalError = error;
        return walletError;
    }
    
    if (typeof error === 'string') {
        return new Error(`${context}: ${error}`);
    }
    
    return new Error(`${context}: ${JSON.stringify(error)}`);
};

/**
 * Wraps a promise to handle wallet errors gracefully
 * @param {Promise} promise - The promise to wrap
 * @param {string} context - Context for error messages
 * @returns {Promise} - Wrapped promise
 */
export const wrapWalletPromise = (promise, context = 'Wallet operation') => {
    return promise.catch(error => {
        if (isWalletError(error)) {
            console.warn('Wallet error detected:', error);
            throw convertWalletError(error, context);
        }
        throw error;
    });
};

/**
 * Creates a safe wallet operation handler
 * @param {Function} operation - The wallet operation function
 * @param {string} context - Context for error messages
 * @returns {Function} - Safe wrapped function
 */
export const createSafeWalletOperation = (operation, context = 'Wallet operation') => {
    return async (...args) => {
        try {
            const result = await operation(...args);
            return result;
        } catch (error) {
            if (isWalletError(error)) {
                console.warn('Wallet operation failed:', error);
                throw convertWalletError(error, context);
            }
            throw error;
        }
    };
};

/**
 * Handles wallet errors in React components
 * @param {any} error - The error to handle
 * @param {string} context - Context for the error
 * @param {Function} onError - Optional error callback
 */
export const handleWalletError = (error, context = 'Wallet operation', onError = null) => {
    if (isWalletError(error)) {
        const walletError = convertWalletError(error, context);
        console.error('Wallet error in', context, ':', walletError);
        
        // Log to Sentry if available
        if (window.Sentry) {
            window.Sentry.captureException(walletError, {
                tags: {
                    errorType: 'WALLET_ERROR',
                    context: context,
                    errorCode: error.code || 'unknown'
                },
                extra: {
                    originalError: error
                }
            });
        }
        
        if (onError) {
            onError(walletError);
        }
        
        return walletError;
    }
    
    return error;
};

/**
 * Sets up global wallet error monitoring
 */
export const setupWalletErrorMonitoring = () => {
    // Monitor for wallet-related unhandled rejections
    window.addEventListener('unhandledrejection', (event) => {
        if (isWalletError(event.reason)) {
            console.warn('Unhandled wallet rejection detected:', event.reason);
            
            // Convert to proper Error
            const walletError = convertWalletError(event.reason, 'Unhandled wallet operation');
            event.reason = walletError;
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(walletError, {
                    tags: {
                        errorType: 'UNHANDLED_WALLET_REJECTION',
                        errorCode: event.reason.code || 'unknown'
                    },
                    extra: {
                        originalError: event.reason.originalError
                    }
                });
            }
        }
    });
};

/**
 * React hook for wallet error handling
 * @param {string} context - Context for error messages
 * @returns {Object} - Hook object with wallet error handling
 */
export const useWalletErrorHandler = (context = 'Wallet operation') => {
    const handleError = React.useCallback((error, onError = null) => {
        return handleWalletError(error, context, onError);
    }, [context]);
    
    const wrapPromise = React.useCallback((promise) => {
        return wrapWalletPromise(promise, context);
    }, [context]);
    
    const safeOperation = React.useCallback((operation) => {
        return createSafeWalletOperation(operation, context);
    }, [context]);
    
    return {
        handleError,
        wrapPromise,
        safeOperation,
        isWalletError
    };
};

export default {
    isWalletError,
    convertWalletError,
    wrapWalletPromise,
    createSafeWalletOperation,
    handleWalletError,
    setupWalletErrorMonitoring,
    useWalletErrorHandler
};
